import { parseDate } from "./quoteLogger";
import { resolveClientScope, scopeTextToHTML } from "./scopeTemplates";
import { SERVICE_CATEGORIES } from "./serviceCategories";
import { legalFooterHTML, loadCompanySettings } from "./companyFooter";

// Optional fields that were left blank are omitted entirely - never printed
// as "—", "To be confirmed", "undefined" or a zero. Only lines with a real
// value make it onto the quote.
// The ONE client-facing Industrial quote: the calculator's Print/Save PDF and
// View Full Quote / Send to Client both call this with the quote's saved
// state, so they show the same content. Client-facing only - no estimator or
// estimated hours (those are on the internal job sheet, see jobSheet.js).
// dateStr: the quote's date ("1 October 2026"); defaults to today.
export async function generateQuoteHTML(data) {
  const {job, service, profile, deploy, notes, finalPrice, subtotalExVat, discount} = data;
  // Company Settings for the legal footer: passed in, or read here.
  const company = data.company !== undefined ? data.company : await loadCompanySettings();

  const date = data.dateStr || new Date().toLocaleDateString("en-GB", {day:"numeric", month:"long", year:"numeric"});
  const catLabel = SERVICE_CATEGORIES.find(s => s.value === service.category)?.label || "";

  let logoSrc = "/logo-dark.png";
  try {
    const resp = await fetch("/logo-dark.png");
    const blob = await resp.blob();
    logoSrc = await new Promise(res => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(blob);
    });
  } catch(e) {}

  const fmtP = (n) => "£" + Number(n||0).toLocaleString("en-GB", {minimumFractionDigits:2, maximumFractionDigits:2});

  // From the saved deployment, so the PDF and View Full Quote always agree.
  const travelMiles = parseFloat(deploy?.travelMiles) || 0;
  const travelRow = travelMiles > 0
    ? `<tr><td>Travel</td><td>${Math.round(travelMiles)} miles</td></tr>`
    : "";
  const overnightRow = deploy.overnight
    ? `<tr><td>Overnight Stays</td><td>${deploy.nights} night${deploy.nights > 1 ? "s" : ""}</td></tr>`
    : "";
  const jobDateParsed = parseDate(job.jobDate);
  const jobDateRow = jobDateParsed
    ? `<p><strong>Job Date:</strong> ${jobDateParsed.toLocaleDateString("en-GB", {day:"2-digit", month:"long", year:"numeric"})}</p>`
    : "";
  // The industrial calculator stores crew per vehicle (deploy.vehicles[].crew),
  // not a single crewSize - sum it, so Team Size isn't printed as "undefined".
  const crew = Number(deploy?.crewSize) || (deploy?.vehicles || []).reduce((n, v) => n + (Number(v.crew) || 0), 0);
  const contaminationText = [profile.contaminationType, profile.severity].filter(Boolean).join(", ");
  const technicalRows = [
    contaminationText && `<tr><td>Contamination</td><td>${contaminationText}</td></tr>`,
    profile.surfaceType && `<tr><td>Surface</td><td>${profile.surfaceType}${profile.sensitivity ? ` (Sensitivity: ${profile.sensitivity})` : ""}</td></tr>`,
    profile.quantity && `<tr><td>Quantity</td><td>${profile.quantity}${profile.quantityUnit ? ` ${profile.quantityUnit}` : ""}</td></tr>`,
    crew > 0 && `<tr><td>Team Size</td><td>${crew} operative${crew > 1 ? "s" : ""}</td></tr>`,
  ].filter(Boolean).join("\n      ");
  // The saved scope exactly as written; the Application template only if it's empty.
  const clientScope = resolveClientScope({ notes, service });
  const scopeNotesSection = clientScope
    ? `<div class="sec"><div class="sectitle">Scope of Work</div><div class="notetext">${scopeTextToHTML(clientScope)}</div></div>`
    : "";
  const exclusionsText = notes.exclusions || "Unless explicitly stated above, all exclusions are in line with our standard terms and conditions.";

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'IBM Plex Sans',sans-serif; color:#0e1520; background:#fff; font-size:12px; line-height:1.6; }
    .page { padding:52px 56px; max-width:794px; margin:0 auto; }
    .topbar { display:none; }
    .hdr { display:flex; justify-content:space-between; align-items:center; margin-bottom:32px; padding-bottom:24px; border-bottom:1px solid #dde2ea; }
    .logo { height:38px; width:auto; }
    .refblock { text-align:right; }
    .reflabel { font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#8a95a3; margin-bottom:4px; }
    .refval { font-size:16px; font-weight:700; color:#0e1520; }
    .refdate { font-size:11px; color:#8a95a3; margin-top:3px; }
    .badge { display:inline-block; background:#eef1fa; color:#1D3461; border-radius:3px; padding:1px 7px; font-size:9px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; margin-left:6px; }
    h1 { font-size:28px; font-weight:700; color:#0e1520; letter-spacing:-0.02em; line-height:1.1; margin-bottom:5px; font-family:'IBM Plex Sans',sans-serif; }
    .sub { font-size:10px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:#8a95a3; margin-bottom:8px; }
    .taglinehdr { font-size:13px; font-style:italic; color:#5a6a7a; margin-bottom:28px; font-weight:400; }
    .twocol { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:28px; }
    .blk { background:#f7f8fa; border-radius:6px; padding:16px 18px; }
    .blktitle { font-size:9px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:#8a95a3; margin-bottom:10px; }
    .blk p { font-size:12px; color:#0e1520; margin-bottom:2px; }
    .blk strong { font-weight:600; }
    .sec { margin-bottom:24px; }
    .sectitle { font-size:10px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:#0e1520; margin-bottom:10px; padding-bottom:8px; border-bottom:1px solid #dde2ea; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th { background:#0e1520; color:#fff; padding:8px 14px; text-align:left; font-size:9px; letter-spacing:0.14em; text-transform:uppercase; font-weight:600; }
    td { padding:9px 14px; border-bottom:1px solid #eef0f5; color:#2a3444; vertical-align:top; }
    tr:last-child td { border-bottom:none; }
    .pricesec { margin:28px 0; padding:24px 0; border-top:2px solid #0e1520; border-bottom:1px solid #dde2ea; }
    .pricemain { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; }
    .pricelabel { font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#8a95a3; margin-bottom:6px; }
    .pricefig { font-size:44px; font-weight:700; color:#0e1520; letter-spacing:-0.03em; line-height:1; font-family:'IBM Plex Sans',sans-serif; }
    .pricevat { font-size:11px; color:#8a95a3; margin-top:5px; }
    .priceterms { text-align:right; padding-top:8px; }
    .priceterms p { font-size:12px; color:#0e1520; font-weight:500; margin-bottom:2px; }
    .tagline { font-size:11px; font-style:italic; color:#8a95a3; margin-top:20px; padding-top:16px; border-top:1px solid #eef0f5; margin-bottom:4px; }
    .notetext { font-size:12px; color:#2a3444; line-height:1.7; }
    .terms { font-size:10px; color:#8a95a3; line-height:1.8; padding-top:16px; border-top:1px solid #eef0f5; margin-top:8px; }
    .footer { margin-top:36px; padding-top:14px; border-top:1px solid #dde2ea; display:flex; justify-content:space-between; font-size:10px; color:#aab0ba; }
    /* Page break rules */
    .blk { page-break-inside: avoid; }
    table { page-break-inside: avoid; }
    .notetext { page-break-inside: avoid; }
    .scope-section { page-break-inside: avoid; }
    .price-section { page-break-before: always; page-break-inside: avoid; }
    .terms { page-break-inside: avoid; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    h1, h2, h3 { page-break-after: avoid; }
    @media print {
  body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  @page { margin: 15mm 10mm; size: a4; }
}
@media screen and (max-width: 600px) {
  .page { padding: 24px 20px !important; }
  .hdr { flex-direction: column; align-items: flex-start; gap: 16px; }
  .refblock { text-align: left !important; }
  .refval { font-size: 13px !important; white-space: nowrap; }
  .twocol { grid-template-columns: 1fr !important; }
  .pricemain { flex-direction: column; gap: 12px; }
  .priceterms { text-align: left !important; }
  .pricefig { font-size: 32px !important; }
  .footer { flex-direction: column; gap: 4px; }
}
  `;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Sublime Quote ${job.quoteRef}</title>
  <style>${css}</style>
</head>
<body>
<div class="page">
  <div class="topbar"></div>
  <div class="hdr">
    <img src="${logoSrc}" class="logo"/>
    <div class="refblock">
      <div class="reflabel">Quote Reference</div>
      <div class="refval">${job.quoteRef}</div>
      <div class="refdate">${date} <span class="badge">Valid 30 days</span></div>
    </div>
  </div>
  <h1>Quotation</h1>
  <div class="sub">Precision Decontamination using Dry Ice Blasting</div>
  <div class="twocol">
    <div class="blk">
      <div class="blktitle">Client Details</div>
      ${job.clientName?`<p><strong>${job.clientName}</strong></p>`:""}
     ${job.clientPOC?`<p>Attn: ${job.clientPOC}</p>`:""}
     ${job.clientEmail?`<p>${job.clientEmail}</p>`:""}
     ${job.clientPhone?`<p>${job.clientPhone}</p>`:""}
      ${job.clientAddress?`<p>${job.clientAddress.replace(/\n/g,"<br/>")}</p>`:""}
    </div>
    <div class="blk">
      <div class="blktitle">Job Details</div>
      ${job.siteName||job.postcode?`<p><strong>Site:</strong> ${[job.siteName,job.postcode].filter(Boolean).join(", ")}</p>`:""}
      ${job.workType?`<p><strong>Work Type:</strong> ${job.workType}</p>`:""}
      ${catLabel?`<p><strong>Category:</strong> ${catLabel}</p>`:""}
      ${service.subService?`<p><strong>Application:</strong> ${service.subService}</p>`:""}
      ${jobDateRow}
    </div>
  </div>
  <div class="sec">
    <div class="sectitle">Technical Summary</div>
    <table>
      <tr><th>Item</th><th>Detail</th></tr>
      <tr><td>Cleaning Method</td><td>Dry Ice CO₂ Blasting: no chemicals, no moisture, non-conductive</td></tr>
      ${technicalRows}
      ${travelRow}${overnightRow}
    </table>
  </div>
  ${scopeNotesSection}
  <div class="sec" style="margin-top:0;">
    <div class="sectitle">What's Included</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <tr><td style="padding:6px 0;border-bottom:1px solid #eef0f5;color:#2a3444;">&#8226; All operative time and labour</td></tr>
      <tr><td style="padding:6px 0;border-bottom:1px solid #eef0f5;color:#2a3444;">&#8226; Dry ice media, consumables and delivery</td></tr>
      <tr><td style="padding:6px 0;border-bottom:1px solid #eef0f5;color:#2a3444;">&#8226; Industry-leading application-specific blasting equipment</td></tr>
      <tr><td style="padding:6px 0;color:#2a3444;">&#8226; Pre and post condition photography</td></tr>
    </table>
  </div>
  <div class="pricesec">
    ${discount && discount.amount > 0 ? `
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:14px;">
      <tr><td style="padding:3px 0;color:#5a6a7a;">Quote total</td><td style="padding:3px 0;text-align:right;color:#5a6a7a;">${fmtP(subtotalExVat)}</td></tr>
      <tr><td style="padding:3px 0;color:#5a6a7a;">${discount.reason || "Discount"}${discount.label ? ` (${discount.label})` : ""}</td><td style="padding:3px 0 3px 12px;text-align:right;color:#b23b3b;white-space:nowrap;vertical-align:top;">−${fmtP(discount.amount)}</td></tr>
    </table>
    ` : ""}
    <div class="pricemain">
      <div>
        <div class="pricelabel">Total ex VAT</div>
        <div class="pricefig">${fmtP(finalPrice).replace("£","£ ")}</div>
        <table style="border-collapse:collapse;font-size:12px;margin-top:6px;">
          <tr><td style="padding:2px 14px 2px 0;color:#5a6a7a;">VAT (20%)</td><td style="padding:2px 0;text-align:right;color:#5a6a7a;">${fmtP(finalPrice*0.2)}</td></tr>
          <tr><td style="padding:2px 14px 2px 0;font-weight:700;color:#0e1520;">Total inc VAT</td><td style="padding:2px 0;text-align:right;font-weight:700;color:#0e1520;">${fmtP(finalPrice*1.2)}</td></tr>
        </table>
      </div>
      <div class="priceterms">
        <p>50% deposit required to confirm booking</p>
        <p>Balance payable within 14 days of completion</p>
      </div>
    </div>
  <div style="margin-top:16px;padding-top:12px;">
    <table style="width:100%;border-collapse:collapse;font-size:11px;color:#8a95a3;">
      ${exclusionsText?`<tr><td style="width:80px;padding:4px 0;vertical-align:top;font-weight:400;color:#8a95a3;">Excludes</td><td style="padding:4px 0;">${exclusionsText}</td></tr>`:""}
    </table>
  </div>
  </div>
  <div style="margin-top:24px;">
    <p style="font-size:10px;font-weight:700;color:#1D3461;letter-spacing:0.18em;text-transform:uppercase;margin:0 0 32px;">We don't clean surfaces. We restore system performance.</p>
  <div class="terms">
    <strong style="color:#5a6a7a">Terms &amp; Conditions</strong><br/>
    Dry ice blasting is a configurable cleaning system tailored to the contamination, substrate, and required outcome.
    All operatives hold relevant safety certifications. Full risk assessment and method statement provided prior to mobilisation.
    Public liability insurance held. Certificates available on request. All prices subject to VAT at the prevailing rate. Valid 30 days from date of issue.
  </div>
  <div class="footer">
    <div>Prepared by Sublime Precision Cleaning, Precision Decontamination for High-Value Assets</div>
    <div>${job.quoteRef} &middot; ${date}</div>
  </div>
  ${legalFooterHTML(company)}
</div>
</body>
</html>`;
}
